import { useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  INTERESTS,
  LANGUAGES,
  type City,
  type Interest,
  type Language,
} from "@nifgash/shared/types";
import { ageSchema, fullNameSchema, profileSchema } from "@nifgash/shared/validators";
import { Avatar } from "../components/Avatar";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { CitySelect } from "../components/CitySelect";
import { ErrorBanner } from "../components/ErrorBanner";
import { Input } from "../components/Input";
import { tg } from "../lib/telegram";
import { updateProfile, uploadPhoto } from "../api/profile";
import { applyReferral } from "../api/points";
import { setSessionUser, type UserRow } from "../api/session";

type State = {
  step: number;
  full_name: string;
  age: string;
  city: City | null;
  interests: Interest[];
  languages: Language[];
  photo: File | null;
  photoPreview: string | null;
};

type Action =
  | { type: "next" }
  | { type: "back" }
  | { type: "set"; patch: Partial<State> }
  | { type: "toggle_interest"; value: Interest }
  | { type: "toggle_language"; value: Language };

type StepKey = "name" | "age" | "city" | "interests" | "languages" | "photo";
const STEP_KEYS: StepKey[] = ["name", "age", "city", "interests", "languages", "photo"];

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case "next":
      return { ...s, step: Math.min(s.step + 1, STEP_KEYS.length - 1) };
    case "back":
      return { ...s, step: Math.max(s.step - 1, 0) };
    case "set":
      return { ...s, ...a.patch };
    case "toggle_interest": {
      const has = s.interests.includes(a.value);
      return { ...s, interests: has ? s.interests.filter((x) => x !== a.value) : [...s.interests, a.value] };
    }
    case "toggle_language": {
      const has = s.languages.includes(a.value);
      return { ...s, languages: has ? s.languages.filter((x) => x !== a.value) : [...s.languages, a.value] };
    }
  }
}

function initState(user: UserRow | null): State {
  return {
    step: 0,
    full_name: user?.full_name ?? "",
    age: user?.age != null ? String(user.age) : "",
    city: (user?.city as City) ?? "haifa",
    interests: (user?.interests ?? []) as Interest[],
    languages: (user?.languages ?? []) as Language[],
    photo: null,
    photoPreview: null,
  };
}

export function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const initial = location.state as { user: UserRow | null } | null;
  const [state, dispatch] = useReducer(reducer, initial?.user ?? null, initState);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isLast = state.step === STEP_KEYS.length - 1;

  // Apply a captured referral now — the user is signed in but hasn't finished
  // onboarding yet, exactly the window applyReferral allows. Clear on success.
  useEffect(() => {
    const ref = localStorage.getItem("nifgash.ref");
    if (!ref) return;
    applyReferral(ref)
      .then(() => localStorage.removeItem("nifgash.ref"))
      .catch(() => {});
  }, []);

  // MainButton
  useEffect(() => {
    const app = tg();
    if (!app) return;
    app.MainButton.setText(isLast ? (submitting ? t("common.saving") : t("common.save")) : t("common.next"));
    app.MainButton.show();
    const onClick = () => {
      if (!submitting) void handleNext();
    };
    app.MainButton.onClick(onClick);
    return () => app.MainButton.offClick(onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, submitting]);

  // BackButton
  useEffect(() => {
    const app = tg();
    if (!app) return;
    if (state.step === 0) {
      app.BackButton.hide();
      return;
    }
    app.BackButton.show();
    const onBack = () => dispatch({ type: "back" });
    app.BackButton.onClick(onBack);
    return () => app.BackButton.offClick(onBack);
  }, [state.step]);

  useEffect(() => () => {
    const app = tg();
    app?.MainButton.hide();
    app?.BackButton.hide();
  }, []);

  function validateCurrent(): string | null {
    switch (state.step) {
      case 0: {
        const r = fullNameSchema.safeParse(state.full_name);
        return r.success ? null : r.error.issues[0]?.message ?? t("register.invalidName");
      }
      case 1: {
        const n = Number(state.age);
        if (!Number.isFinite(n)) return t("register.invalidAgeNumber");
        const r = ageSchema.safeParse(n);
        return r.success ? null : r.error.issues[0]?.message ?? t("register.invalidAge");
      }
      case 2:
        return state.city ? null : t("register.pickCity");
      case 3:
        return state.interests.length ? null : t("register.pickInterest");
      case 4:
        return state.languages.length ? null : t("register.pickLang");
      case 5:
        return null;
    }
    return null;
  }

  async function handleNext() {
    const err = validateCurrent();
    if (err) {
      setError(err);
      tg()?.HapticFeedback?.impactOccurred("medium");
      return;
    }
    setError(null);
    if (!isLast) {
      dispatch({ type: "next" });
      return;
    }
    await submit();
  }

  async function submit() {
    setSubmitting(true);
    try {
      const payload = profileSchema.parse({
        full_name: state.full_name.trim(),
        age: Number(state.age),
        city: state.city,
        interests: state.interests,
        languages: state.languages,
      });
      const user = await updateProfile(payload);
      if (state.photo) await uploadPhoto(state.photo);
      setSessionUser(user);
      tg()?.HapticFeedback?.impactOccurred("light");
      navigate("/welcome-bonus", { replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(t("register.saveFailed", { msg }));
    } finally {
      setSubmitting(false);
    }
  }

  const curKey = STEP_KEYS[state.step]!;

  return (
    <div className="min-h-screen bg-surface pb-24">
      {/* Progress + label */}
      <div className="px-5 pt-6 pb-2">
        <div className="flex items-center justify-between text-[12px] font-bold uppercase tracking-wider text-ink-400 mb-3">
          <span>{t("register.stepProgress", { step: state.step + 1, total: STEP_KEYS.length })}</span>
          <span>{t(`register.steps.${curKey}`)}</span>
        </div>
        <div className="h-1.5 bg-ink-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-purple rounded-full transition-all duration-300"
            style={{ width: `${((state.step + 1) / STEP_KEYS.length) * 100}%`, transitionTimingFunction: "var(--ease-out)" }}
          />
        </div>
      </div>

      {/* Step heading */}
      <div className="px-5 mt-6 mb-2">
        <h1 className="text-[28px] font-extrabold text-ink-900 tracking-tight leading-tight">
          {t(`register.hints.${curKey}`)}
        </h1>
      </div>

      {/* Error */}
      {error && <ErrorBanner message={error} />}

      {/* Step body */}
      <div className="px-5 mt-4">
        {state.step === 0 && (
          <Input
            placeholder={t("register.namePlaceholder")}
            value={state.full_name}
            autoFocus
            onChange={(e) => dispatch({ type: "set", patch: { full_name: e.target.value } })}
          />
        )}
        {state.step === 1 && (
          <Input
            type="number"
            inputMode="numeric"
            placeholder={t("register.agePlaceholder")}
            value={state.age}
            autoFocus
            onChange={(e) => dispatch({ type: "set", patch: { age: e.target.value } })}
          />
        )}
        {state.step === 2 && (
          <CitySelect value={state.city} onChange={(c) => c && dispatch({ type: "set", patch: { city: c } })} />
        )}
        {state.step === 3 && (
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map((i) => (
              <Chip key={i} size="md" selected={state.interests.includes(i)} onClick={() => dispatch({ type: "toggle_interest", value: i })}>
                {t(`labels.interest.${i}`)}
              </Chip>
            ))}
          </div>
        )}
        {state.step === 4 && (
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((l) => (
              <Chip key={l} size="md" selected={state.languages.includes(l)} onClick={() => dispatch({ type: "toggle_language", value: l })}>
                {t(`labels.language.${l}`)}
              </Chip>
            ))}
          </div>
        )}
        {state.step === 5 && (
          <StepPhoto
            t={t}
            file={state.photo}
            preview={state.photoPreview}
            name={state.full_name}
            setFile={(f, p) => dispatch({ type: "set", patch: { photo: f, photoPreview: p } })}
          />
        )}
      </div>

      {/* Bottom CTA (fallback when no MainButton) */}
      <div className="fixed bottom-0 left-0 right-0 px-5 py-4 bg-surface border-t border-ink-100">
        <div className="flex gap-3 max-w-[560px] mx-auto">
          {state.step > 0 && (
            <Button size="lg" variant="secondary" onClick={() => dispatch({ type: "back" })} disabled={submitting}>
              {t("common.back")}
            </Button>
          )}
          <Button stretched size="lg" onClick={() => void handleNext()} disabled={submitting}>
            {isLast ? (submitting ? t("common.saving") : t("common.save")) : t("common.next")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StepPhoto({
  t,
  file,
  preview,
  name,
  setFile,
}: {
  t: TFunction;
  file: File | null;
  preview: string | null;
  name: string;
  setFile: (f: File | null, p: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col items-center gap-5 mt-2">
      <div className="relative">
        <Avatar url={preview} name={name} size={140} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="absolute bottom-0 right-0 w-11 h-11 rounded-full bg-brand-purple text-white flex items-center justify-center shadow-[var(--shadow-card)] active:scale-95 transition-all"
          aria-label={t("register.choosePhoto")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null;
          if (!f) return;
          if (f.size > 5 * 1024 * 1024) {
            alert(t("register.photoTooLarge"));
            return;
          }
          const reader = new FileReader();
          reader.onload = (ev) => setFile(f, String(ev.target?.result ?? ""));
          reader.readAsDataURL(f);
        }}
      />
      {file ? (
        <button onClick={() => setFile(null, null)} className="text-[14px] font-semibold text-brand-red">
          {t("register.removePhoto")}
        </button>
      ) : (
        <p className="text-[14px] text-ink-500">{t("register.skipPhoto")}</p>
      )}
    </div>
  );
}
